import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import {
  getOutsourceTechnicians,
  createOutsourceTechnician,
  updateOutsourceTechnician,
  deleteOutsourceTechnician
} from '../../services/api';
import { isFullAdmin } from '../../utils/auth';
import ExportButtons from '../../components/ExportButtons';
import { dash } from '../../utils/tableExport';

const emptyForm = { name: '', phone: '', notes: '', is_active: true };

const OutsourceTechniciansPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getOutsourceTechnicians({ all: '1' });
      setRows(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load technicians');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isFullAdmin()) {
      navigate('/repairs');
      return;
    }
    load();
  }, [load, navigate]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      phone: row.phone || '',
      notes: row.notes || '',
      is_active: row.is_active !== false
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      if (!form.name.trim()) {
        setError('Name is required');
        return;
      }
      if (editing) {
        await updateOutsourceTechnician(editing.id, form);
        setSuccess('Technician updated');
      } else {
        await createOutsourceTechnician(form);
        setSuccess('Technician added');
      }
      setOpen(false);
      load();
    } catch (err) {
      setError(err.message || 'Save failed');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"?`)) return;
    try {
      await deleteOutsourceTechnician(row.id);
      setSuccess('Technician deactivated');
      load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  if (!isFullAdmin()) return null;

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">Outsource Technicians</Typography>
          <Typography color="text.secondary">
            Admin / Super Admin only — names appear in the repair page dropdown
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ExportButtons
            title="Outsource Technicians"
            filename="outsource_technicians"
            columns={[
              { label: 'Name', value: (r) => dash(r.name) },
              { label: 'Phone', value: (r) => dash(r.phone) },
              { label: 'Status', value: (r) => (r.is_active ? 'Active' : 'Inactive') },
              { label: 'Notes', value: (r) => dash(r.notes) }
            ]}
            rows={rows}
            onError={setError}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Technician
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell width={100} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5}>Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary">No technicians yet. Click Add Technician.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.phone || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.is_active ? 'Active' : 'Inactive'}
                      color={row.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{row.notes || '—'}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Technician' : 'Add Outsource Technician'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OutsourceTechniciansPage;
